import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import apiClient, { formatApiError } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useResendVerification } from '@/hooks/useResendVerification';
import { devLog } from '@/utils/logger';

/**
 * VerifyEmail Page
 *
 * Handles email verification via a one-time token link:
 *   /verify-email/:token
 *
 * States:
 *  - verifying: POST token to /api/auth/verify-email, show spinner
 *  - success:   show confirmation + CTA to dashboard or login
 *  - error:     show message; if logged in, offer resend button
 *
 * Public route — accessible whether or not the user is logged in.
 */

type VerifyStatus = 'verifying' | 'success' | 'error';

interface VerifyApiData {
  verified: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const VerifyEmail = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();

  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  const { status: resendStatus, resend } = useResendVerification();
  // Token the request was already fired for: StrictMode double-invoked effects and
  // reloads must not consume the one-time token twice.
  const [tokenAlreadyUsed, setTokenAlreadyUsed] = useState(false);
  const verifiedTokenRef = useRef<string | null>(null);

  // POST the token on mount
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('auth_verify_email_error_invalid'));
      return;
    }

    if (verifiedTokenRef.current === token) return;
    verifiedTokenRef.current = token;

    const verify = async () => {
      try {
        const response = await apiClient.post<ApiResponse<VerifyApiData>>(
          '/api/auth/verify-email',
          { token },
        );

        if (response.data.success && response.data.data.verified) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage(t('auth_verify_email_error_generic'));
        }
      } catch (err) {
        devLog.error('[VerifyEmail] Verification failed:', err);
        const apiError = formatApiError(err);
        // Token already consumed (double request, reload, link reopened): if the signed-in
        // account is in fact verified, this is a success, not a failure.
        if (apiError.code === 'verification_invalid') setTokenAlreadyUsed(true);
        if (apiError.code === 'verification_invalid' && isAuthenticated) {
          try {
            const me = await refreshUser();
            if (me.emailVerified) {
              setStatus('success');
              return;
            }
          } catch (refreshError) {
            devLog.error('[VerifyEmail] Could not re-check account after used token:', refreshError);
          }
        }
        setStatus('error');
        // Backend sends code 'verification_expired' for expired tokens
        if (apiError.code === 'verification_expired') {
          setErrorMessage(t('auth_verify_email_error_expired'));
        } else {
          setErrorMessage(apiError.message || t('auth_verify_email_error_generic'));
        }
      }
    };

    void verify();

    // token is stable for this page mount; t is referentially stable after i18n load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth may finish loading after the failed POST: a used token plus an already-verified
  // signed-in account is a success.
  useEffect(() => {
    if (status === 'error' && tokenAlreadyUsed && user?.emailVerified) setStatus('success');
  }, [status, tokenAlreadyUsed, user?.emailVerified]);

  // --- Render helpers ---

  const renderContent = () => {
    if (status === 'verifying') {
      return (
        <div className="text-center py-4">
          <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-6 font-wagner">
            {t('auth_verify_email_verifying')}
          </h1>
          <div className="flex justify-center">
            <svg
              className="animate-spin h-10 w-10 text-bs-secondary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="text-center py-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-3 font-wagner">
            {t('auth_verify_email_success_heading')}
          </h1>

          <p className="text-sm text-gray-600 mb-8">
            {t('auth_verify_email_success_message')}
          </p>

          <div className="flex items-center justify-center">
            {isAuthenticated ? (
              <Button
                type="button"
                variant="primary"
                size="lg"
                onClick={() => navigate('/')}
                className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
              >
                {t('auth_verify_email_success_cta')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="lg"
                onClick={() => navigate('/login')}
                className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
              >
                {t('auth_verify_email_success_cta_login')}
              </Button>
            )}
          </div>
        </div>
      );
    }

    // error state
    return (
      <div className="text-center py-2">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>

        <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-3 font-wagner">
          {t('auth_verify_email_error_heading')}
        </h1>

        <div
          className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
          role="alert"
        >
          {errorMessage}
        </div>

        {/* Resend option — only available for logged-in users */}
        {isAuthenticated && user && (
          <div className="mb-6">
            {resendStatus === 'sent' ? (
              <p className="text-sm text-green-600 font-medium">
                {t('auth_verify_email_resend_sent')}
              </p>
            ) : resendStatus === 'error' ? (
              <p className="text-sm text-red-500">
                {t('auth_verify_email_resend_error')}
              </p>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="lg"
                loading={resendStatus === 'sending'}
                onClick={resend}
                disabled={resendStatus === 'sending'}
                className="uppercase tracking-wide w-full sm:w-auto sm:px-10 !h-9 sm:!h-auto !text-xs sm:!text-base"
              >
                {resendStatus === 'sending'
                  ? t('auth_verify_email_resend_sending')
                  : t('auth_verify_email_resend_button')}
              </Button>
            )}
          </div>
        )}

        <div className="flex items-center justify-center mt-4 mb-2">
          <Link
            to="/login"
            className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
          >
            &larr; {t('auth_verify_email_back_to_login')}
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      {/* Card — mirrors Login / Register card layout */}
      <div className="relative bg-white rounded-lg shadow-2xl px-7 pt-6 pb-3 sm:px-8 sm:pt-8 sm:pb-4 w-full max-w-lg flex flex-col">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="w-full max-w-[35%] sm:max-w-[45%] h-auto"
            onError={(e) => {
              devLog.log('[VerifyEmail] Logo failed to load:', e.currentTarget.src);
              e.currentTarget.onerror = null;
            }}
          />
        </div>

        {/* Tagline — desktop only */}
        <div className="hidden sm:block text-center mb-4 sm:mb-6">
          <p className="text-[11px] uppercase tracking-wider text-gray-900 leading-relaxed font-medium">
            {t('auth_register_tagline_creating')}{' '}
            <span className="text-bs-secondary font-bold">{t('auth_register_tagline_brand_value')}</span>.
            <br />
            {t('auth_register_tagline_one_asset')}
            <br />
            {t('auth_register_tagline_content_marketing')}{' '}
            <span className="text-bs-secondary font-bold">{t('auth_register_tagline_next_level')}</span>.
          </p>
        </div>

        {/* Dynamic content */}
        {renderContent()}

        {/* Footer */}
        {/* eslint-disable i18next/no-literal-string */}
        <div className="mt-auto border-t border-gray-200" />
        <p className="pt-3 text-[10px] sm:text-xs text-gray-400 text-center">
          &copy; {new Date().getFullYear()} Made with{' '}
          <span className="text-bs-secondary">&#10084;&#65039;</span> by{' '}
          <a
            href="https://overw8.de"
            target="_blank"
            rel="noopener noreferrer"
            className="text-bs-secondary hover:text-secondary-600 font-medium transition-colors"
          >
            OVERW8
          </a>
          <span className="mx-1 text-gray-300">&middot;</span>
          <a
            href="https://agile-assets.ai/house-rules/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-secondary-500 transition-colors"
          >
            Terms
          </a>
          <span className="mx-1 text-gray-300">&middot;</span>
          <a
            href="https://agile-assets.ai/packages-pricing/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-secondary-500 transition-colors"
          >
            Pricing
          </a>
          <span className="mx-1 text-gray-300">&middot;</span>
          <a
            href="https://agile-assets.ai/about/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-secondary-500 transition-colors"
          >
            About
          </a>
        </p>
        {/* eslint-enable i18next/no-literal-string */}
      </div>
    </div>
  );
};
